import { useEffect, useRef } from "react";
import { api } from "@/lib/api";

/**
 * useWatchHeartbeat — fires POST /channels/{channel}/watch/heartbeat every
 * 60 seconds while the user is authenticated and the page is visible.
 *
 * The server bumps `viewer_watch_time.total_seconds` by 60 each time, capped
 * to one bump per 30-sec window per (viewer, streamer). Combined with the
 * page-visibility check on the client, this means "hours watched" only
 * counts foreground viewing — closing the tab or switching away pauses it.
 */
export function useWatchHeartbeat(channel, { enabled = true, intervalMs = 60000 } = {}) {
  const timerRef = useRef(null);
  useEffect(() => {
    if (!enabled || !channel) return () => {};
    const token = typeof window !== "undefined" ? localStorage.getItem("sc_token") : null;
    if (!token) return () => {};

    const beat = async () => {
      if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
      try { await api.post(`/channels/${channel}/watch/heartbeat`); } catch { /* silent */ }
    };
    beat(); // fire once immediately
    timerRef.current = setInterval(beat, intervalMs);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [channel, enabled, intervalMs]);
}
